import React, { useEffect, useState } from "react";
import Table from "../../../../core/pagination/datatable";
import { Spin } from "antd";
import dayjs from "dayjs";
import { BASE_CRM_URL } from "../../../components/config/config";

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");

  const staffId = userDetails?.staffid;

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      setTasksLoading(true);

      const response = await fetch(
        `${BASE_CRM_URL}tasks/GetTasksnew?assigned=${staffId}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch tasks");
      }

      const data = await response.json();

      if (Array.isArray(data?.ResultData)) {
        setTasks(data.ResultData);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  const avatarColors = [
    { background: "#E7F1FF", color: "#0D6EFD" },
    { background: "#E8F8F0", color: "#198754" },
    { background: "#FFF4DE", color: "#FF9800" },
    { background: "#FDECEC", color: "#DC3545" },
    { background: "#E8F7FA", color: "#0DCAF0" },
    { background: "#F3E8FF", color: "#6F42C1" },
  ];

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "complete":
      case "completed":
        return "badge bg-soft-success";

      case "testing":
        return "badge bg-soft-primary";

      case "in progress":
        return "badge bg-soft-warning text-dark";

      case "not started":
        return "badge bg-soft-danger";

      default:
        return "badge bg-soft-secondary";
    }
  };

  const getPriorityClass = (priority) => {
    switch (Number(priority)) {
      case 1:
        return "badge bg-soft-success";

      case 2:
        return "badge bg-soft-warning text-dark";

      case 3:
        return "badge bg-soft-danger";

      default:
        return "badge bg-soft-secondary";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (Number(priority)) {
      case 1:
        return "Low";

      case 2:
        return "High";

      case 3:
        return "Urgent";

      default:
        return "N/A";
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (_, record) => (
        <div>
          <strong>
            {record.name?.length > 15
              ? `${record.name.substring(0, 15)}...`
              : record.name || "N/A"}
          </strong>

          {record.description && (
            <>
              <br />
              <span
                style={{
                  color: "gray",
                  fontSize: "12px",
                }}
              >
                {record.description.length > 20
                  ? `${record.description.substring(0, 20)}...`
                  : record.description}
              </span>
            </>
          )}
        </div>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => (
        <span className={getStatusClass(status)}>{status || "N/A"}</span>
      ),
    },

    {
      title: "Start Date",
      dataIndex: "startdate",
      key: "startdate",
      sorter: (a, b) => new Date(a.startdate || 0) - new Date(b.startdate || 0),
      render: (date) => (
        <span>
          <i className="fa-solid fa-calendar-plus text-primary me-2"></i>
          {date ? dayjs(date).format("DD/MM/YYYY") : "N/A"}
        </span>
      ),
    },

    {
      title: "Due Date",
      dataIndex: "duedate",
      key: "duedate",
      sorter: (a, b) => new Date(a.duedate || 0) - new Date(b.duedate || 0),
      render: (date) => (
        <span>
          <i className="fa-solid fa-calendar-check text-danger me-2"></i>
          {date ? dayjs(date).format("DD/MM/YYYY") : "N/A"}
        </span>
      ),
    },

    {
      title: "Assigned To",
      dataIndex: "staff_names",
      key: "staff_names",
      sorter: (a, b) =>
        (a.staff_names || "").localeCompare(b.staff_names || ""),
      render: (staffNames) => {
        if (!staffNames) {
          return "N/A";
        }

        const names = staffNames
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean);

        return (
          <div className="avatar-list-stacked d-flex">
            {names.map((name, index) => {
              const color = avatarColors[index % avatarColors.length];

              return (
                <span
                  key={`${name}-${index}`}
                  className="avatar avatar-rounded fw-bold d-flex justify-content-center align-items-center"
                  style={{
                    width: 35,
                    height: 35,
                    fontSize: 14,
                    backgroundColor: color.background,
                    color: color.color,
                  }}
                  title={name}
                >
                  {name.charAt(0).toUpperCase()}
                </span>
              );
            })}
          </div>
        );
      },
    },

    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      sorter: (a, b) => Number(a.priority || 0) - Number(b.priority || 0),
      render: (priority) => (
        <span className={getPriorityClass(priority)}>
          {getPriorityLabel(priority)}
        </span>
      ),
    },
  ];

  return (
    <div className="card table-list-card">
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
        <div className="search-set p-3"></div>
      </div>

      <div className="card-body">
        <div className="table-responsive">
          {tasksLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "250px", // adjust height if needed
              }}
            >
              <Spin size="large" />
            </div>
          ) : (
            <Table
              key={`tasks-${tasks.map((task) => task.id).join("-")}`}
              columns={columns}
              dataSource={tasks}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Tasks;
